import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  getToken,
  removeToken,
  selectAdminUser,
  selecthasToken,
} from "./homeSlice";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Menu,
  colors,
  Assets,
  MonetizationOnOutlined,
  SearchOutlined,
  StorefrontOutlined,
  SwitchAccountOutlined,
  PropaneTankOutlined,
  ContactMailOutlined,
  AccountBalanceOutlined,
} from "@qivia/ui";
import useAuth from "./useAuth";
import "../i18n/i18n";
import { BadgeLogo, Car } from "@qivia/ui/src/assets/assets";
import { devTeam } from "../constants";

export const Home = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const adminUser = useAppSelector(selectAdminUser);
  const hasToken = useAppSelector(selecthasToken);
  const location = useLocation();
  useAuth();

  const initialSelectedRoute = location.pathname.split("/")[2]
    ? location.pathname.split("/")[2]
    : "";
  const [selectedRoute, setSelectedRoute] = useState<string | undefined>(
    initialSelectedRoute,
  );

  const env = import.meta.env["VITE_TARGET_ENV"] as string;
  type EnvType = "production" | "staging" | "demo" | "localhost";
  const colorHeaderDict = {
    production: {
      color: colors.red,
    },
    staging: {
      color: colors.blue,
    },
    demo: {
      color: colors.paleGreen,
    },
    localhost: {
      color: colors.contentSecondary,
    },
  };

  useEffect(() => {
    if (!adminUser) {
      if (hasToken === false) {
        navigate("/login");
      }
      dispatch(getToken());
    }
  }, [adminUser, dispatch, hasToken, navigate]);

  const logout = () => {
    dispatch(removeToken());
    navigate("/login");
  };

  const onChangeRoute = (route: string) => {
    setSelectedRoute(route);
    navigate(route);
  };

  const hasExterieurEmail = useMemo(
    () => adminUser?.email === "exterieur1@qivia.fr",
    [adminUser],
  );

  const isAuthorizedAdmin = useMemo(
    () => devTeam.includes(adminUser?.email ?? ""),
    [adminUser],
  );

  const props = {
    sections: [
      {
        route: "supporting_documents",
        iconSrc: (
          <StyledIcon>
            <SearchOutlined />
          </StyledIcon>
        ),
        title: t("home.menu.supportingDocuments"),
        canExterieurAccess: true,
      },
      {
        route: "merchant_fix",
        iconSrc: <Assets.AlertBlack />,
        title: t("home.menu.merchantFix"),
        canExterieurAccess: true,
      },
      {
        route: "drivers",
        iconSrc: (
          <StyledIcon>
            <SwitchAccountOutlined />
          </StyledIcon>
        ),
        title: t("home.menu.drivers"),
        canExterieurAccess: true,
      },
      {
        route: "vehicles",
        iconSrc: (
          <StyledIcon>
            <Car />
          </StyledIcon>
        ),
        title: t("home.menu.vehicles"),
        canExterieurAccess: true,
      },
      {
        route: "tank_capacities",
        iconSrc: (
          <StyledIcon>
            <PropaneTankOutlined />
          </StyledIcon>
        ),
        title: t("home.menu.tankCapacities"),
        canExterieurAccess: true,
      },
      {
        route: "cards",
        iconSrc: (
          <StyledIcon>
            <ContactMailOutlined />
          </StyledIcon>
        ),
        title: t("home.menu.cards"),
        canExterieurAccess: true,
      },
      {
        route: "badges",
        iconSrc: (
          <StyledIcon>
            <BadgeLogo />
          </StyledIcon>
        ),
        title: t("home.menu.badges"),
        canExterieurAccess: true,
      },
      {
        route: "electric",
        iconSrc: <Assets.ElectricBolt />,
        title: t("home.menu.electric"),
        canExterieurAccess: true,
      },
      {
        route: "companies_data",
        iconSrc: (
          <StyledIcon>
            <StorefrontOutlined />
          </StyledIcon>
        ),
        title: t("home.menu.companiesData"),
        canExterieurAccess: true,
      },
      {
        route: "mandates_and_sepa_direct_debits",
        iconSrc: (
          <StyledIcon>
            <AccountBalanceOutlined />
          </StyledIcon>
        ),
        title: t("home.menu.mandatesAndSepaDirectDebits"),
        canExterieurAccess: true,
      },
      {
        route: "billings",
        iconSrc: (
          <StyledIcon>
            <MonetizationOnOutlined />
          </StyledIcon>
        ),
        title: t("home.menu.billings"),
      },
      {
        route: "statements",
        iconSrc: <Assets.Billing />,
        title: t("home.menu.statement"),
      },
      {
        route: "qrCode",
        iconSrc: <Assets.QrCode />,
        title: t("home.menu.qrCode"),
      },
      {
        route: "termsAndPolicy",
        iconSrc: <Assets.DownloadSolid />,
        title: t("home.menu.termsAndPolicy"),
      },
      {
        route: "telemetry",
        iconSrc: <Assets.Car />,
        title: t("home.menu.telemetry"),
      },
      ...(isAuthorizedAdmin
        ? [
            {
              route: "debug_account",
              iconSrc: (
                <StyledIcon>
                  <SwitchAccountOutlined />
                </StyledIcon>
              ),
              title: t("home.menu.debugAccount"),
            },
          ]
        : []),
      {
        route: "",
        iconSrc: <Assets.Home />,
        title: t("home.menu.home"),
      },
    ],
    route: "users",
    onChangeRoute: onChangeRoute,
    logout: logout,
    isQg: true,
    hasExterieurEmail,
  };

  const colorHeader =
    colorHeaderDict[env as EnvType]?.color || colors.contentSecondary;

  return (
    <StyledContainer>
      <StyledHeader
        color={colorHeader}
        $isInProduction={(env as EnvType) === "production"}
      >
        {t(`home.header.warning.${env as EnvType}`)} {adminUser?.name}
      </StyledHeader>
      <StyledContent>
        <Menu {...props} selectedRoute={selectedRoute} />
        <StyledAbsolute id="refreshSessionGoogleButton" />
        <Outlet />
      </StyledContent>
    </StyledContainer>
  );
};

const StyledAbsolute = styled.div`
  position: absolute;
  z-index: 1;
  left: 40%;
  top: 5%;
`;
const StyledContent = styled.div`
  display: flex;
  flex-direction: row;
  height: 96%;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
`;

const StyledIcon = styled.div`
  & svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const StyledHeader = styled.div<{ color: string; $isInProduction: boolean }>`
  display: flex;
  background-color: ${({ color }) => color};
  color: white;
  height: 4%;
  justify-content: center;
  align-items: center;
  ${({ $isInProduction }) =>
    $isInProduction &&
    `animation-duration: 6s;
  animation-name: clignoter;
  animation-iteration-count: infinite;
  transition: none;
  @keyframes clignoter {
    0% {
      opacity: 1;
    }
    40% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }`}
`;
